import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { APPARITIONS, FLAGSHIP_IDS, CHRONOLOGICAL, STATUS_LABELS, byId } from "@/data/apparitions";
import { Reveal, SectionHeading, EASE } from "./Reveal";

export const StatusChip = ({ labelKey, className = "" }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full border border-gold/40 bg-gold/10 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.16em] text-midnight ${className}`}
  >
    <span className="h-1 w-1 rounded-full bg-gold" aria-hidden="true" />
    {STATUS_LABELS[labelKey]}
  </span>
);

export function ApparitionCard({ entry, delay = 0, headingLevel = "h3" }) {
  const Heading = headingLevel;
  return (
    <Reveal delay={delay} className="h-full">
      <article
        data-testid={`apparition-card-${entry.id}`}
        className="card-lift group flex h-full flex-col overflow-hidden rounded-2xl border border-marian/15 bg-white/90 backdrop-blur"
      >
        <Link to={`/apparitions/${entry.id}`} data-testid={`apparition-image-link-${entry.id}`} className="relative block h-56 overflow-hidden" aria-label={`${entry.title} — read the story`}>
          <img
            src={entry.image.url}
            alt={entry.image.alt}
            loading="lazy"
            className={`h-full w-full ${entry.image.contain ? "object-contain" : "object-cover"} ${entry.image.position || "object-center"} transition-transform duration-700 ease-out ${entry.image.contain ? "" : "group-hover:scale-105"}`}
          />
          <div className="absolute inset-0 bg-[linear-gradient(to_top,rgba(16,42,67,0.55),transparent_50%)]" aria-hidden="true" />
          <span className="absolute bottom-3 left-4 font-mono text-[10px] uppercase tracking-[0.22em] text-ivory/90">
            {entry.country} · {entry.year}
          </span>
        </Link>
        <div className="flex flex-1 flex-col p-6">
          <Heading data-testid={`apparition-heading-${entry.id}`} className="font-serif text-xl font-semibold leading-tight text-midnight sm:text-2xl">
            {entry.title}
          </Heading>
          <p className="mt-2 text-sm text-charcoal/75">{entry.themes.join(" · ")}</p>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {entry.labels.map((l) => (
              <StatusChip key={l} labelKey={l} />
            ))}
          </div>
          <Link
            to={`/apparitions/${entry.id}`}
            data-testid={`apparition-read-${entry.id}`}
            className="group/btn mt-5 inline-flex items-center gap-2 self-start text-sm font-semibold text-marian transition-colors hover:text-midnight"
          >
            Read the story
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-1" />
          </Link>
        </div>
      </article>
    </Reveal>
  );
}

export default function Apparitions() {
  const [showAll, setShowAll] = useState(false);
  const flagships = FLAGSHIP_IDS.map(byId);

  return (
    <section id="apparitions" data-testid="featured-apparitions" className="py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeading eyebrow="The featured collection" title="Marian apparitions & living devotions">
            Places where Catholics have heard powerful calls to prayer, conversion,
            reconciliation, and hope. Each page names its exact Church status and its sources.
          </SectionHeading>
          <Reveal delay={0.1}>
            <Link
              to="/apparitions"
              data-testid="browse-collection-link"
              className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-marian transition-colors hover:text-midnight"
            >
              Browse the collection
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {flagships.map((entry, i) => (
            <ApparitionCard key={entry.id} entry={entry} delay={i * 0.08} />
          ))}
        </div>

        <Reveal delay={0.1} className="mt-10 text-center">
          <button
            type="button"
            data-testid="view-all-16-button"
            onClick={() => setShowAll((v) => !v)}
            aria-expanded={showAll}
            className="group inline-flex items-center gap-2.5 rounded-full border border-marian/35 bg-white/80 px-7 py-3 text-sm font-semibold text-marian transition-all duration-300 hover:border-gold hover:text-midnight"
          >
            {showAll ? "Close the collection" : `View all ${APPARITIONS.length} entries`}
            <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${showAll ? "rotate-180" : ""}`} />
          </button>
        </Reveal>

        <AnimatePresence>
          {showAll && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.6, ease: EASE }}
              className="overflow-hidden"
            >
              <div className="mt-8 grid gap-3 rounded-2xl border border-marian/15 bg-white/70 p-5 sm:grid-cols-2 sm:p-7 lg:grid-cols-3" data-testid="sixteen-list">
                {CHRONOLOGICAL.map((item) => (
                  <Link
                    key={item.id}
                    to={`/apparitions/${item.id}`}
                    data-testid={`sixteen-item-${item.id}`}
                    className="group rounded-xl border border-marian/10 bg-ivory px-5 py-4 transition-colors hover:border-gold/50"
                  >
                    <p className="flex items-center justify-between font-serif text-lg font-semibold text-midnight">
                      {item.title}
                      <ArrowRight className="h-4 w-4 text-gold opacity-0 transition-all duration-300 group-hover:translate-x-1 group-hover:opacity-100" />
                    </p>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-marian">
                      {item.place} · {item.year}
                    </p>
                    <p className="mt-2 text-xs text-slateblue">{STATUS_LABELS[item.labels[0]]}</p>
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
